import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-service password-reset request page.
 *
 * The backend endpoint /api/auth/password-reset/request is intentionally
 * anti-enumeration: it always returns 200 regardless of whether the email
 * exists or Resend is configured. The UI mirrors that by ALWAYS showing a
 * generic confirmation on submit and never revealing whether the email
 * matched an admin account.
 */
export default function AdminForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email });
    } catch (err) {
      // Deliberately swallow — the endpoint is anti-enumeration. We should
      // never disclose whether the address is registered.
      void err;
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-[calc(100vh-180px)] flex items-center justify-center px-4 py-12" data-testid="admin-forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/ccchaps-logo.png" alt="California Corporate Chaplains" className="h-16 w-16 object-contain mx-auto mb-5" />
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">Reset your password</h1>
          <p className="mt-2 text-sm text-slate-500">Enter your CCCHAPS admin email to request a reset link.</p>
        </div>

        {submitted ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm text-center" data-testid="admin-forgot-password-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mx-auto mb-4">
              <Mail className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              If an account exists for that email address, a password reset link has been sent.
            </p>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed">
              Didn&apos;t receive one within a few minutes? Check spam, or contact your CCCHAPS administrator to have a reset link issued manually.
            </p>
            <Link
              to="/care-portal/admin/login"
              data-testid="admin-forgot-password-back-to-login"
              className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Return to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm space-y-5" data-testid="admin-forgot-password-form">
            <label className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1.5">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-forgot-password-email"
                autoComplete="email"
                className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-forgot-password-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Send reset link"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <Link
                to="/care-portal/admin/login"
                data-testid="admin-forgot-password-back-link"
                className="inline-flex items-center gap-1 text-slate-500 hover:text-[#4A6FA5]"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
              <span className="text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5]" /> Anti-enumeration
              </span>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
